/**
 * How the townsfolk's life acts look (lane C0-NPC; shared data/npcLife.ts, systems/npcLife.ts): each act's emote id
 * (`life-<act>`, `lifeEmote`) as a run of rig poses at a rate, played by the emote channel like any emote
 * (render/emotes.ts takes these in). The work poses with tools (hoe, broom, rod, net, cup, shoulder pole) come from
 * render/motions/folk.ts, the sitting ones from core (`sit`, `drink`), the new ones from render/motions/life.ts.
 */
import { LIFE_ACT_IDS, lifeEmote, type LifeActId } from '@nine/shared';
import type { EmoteArt } from './emotes';
import { FRAMES, motionPhase, type Pose } from './sprites';

/** A two-beat loop of `FRAMES` frames: phase 0 of `pose` for `share` of `seconds`, then phase 1. */
const beat = (pose: Pose, share: number, seconds: number): EmoteArt => ({
  frames: Array.from({ length: FRAMES }, (_, i) => [pose, i < Math.round(FRAMES * share) ? 0 : 1] as const),
  fps: FRAMES / seconds,
});

/** A looping key pose run round its `FRAMES` phases in `seconds`. */
const loop = (pose: Pose, seconds: number): EmoteArt => ({
  frames: Array.from({ length: FRAMES }, (_, i) => [pose, i] as const),
  fps: FRAMES / seconds,
});

/** A still. */
const still = (pose: Pose, ph = 0): EmoteArt => ({ frames: [[pose, ph]], fps: 1 });

/** Poses one after another, each held `hold` frames (of `FRAMES` a second-ish). */
const run = (keys: readonly (readonly [Pose, number, number])[], fps = 6): EmoteArt => ({
  frames: keys.flatMap(([p, ph, n]) => Array.from({ length: n }, () => [p, ph] as const)),
  fps,
});

/** Sitting with a cup or bowl: now and then it goes up to the lips. */
const sipping = run([
  ['sit', 0, 8],
  ['drink', 0, 5],
  ['sit', 0, 5],
]);

const ART: Readonly<Record<LifeActId, EmoteArt>> = {
  eatInn: beat('lifeSitEat', 0.55, 2.2),
  eatStall: beat('lifeEatStand', 0.6, 2.2),
  eatHome: beat('lifeSitEat', 0.6, 2.4),
  snack: beat('lifeEatStand', 0.65, 2),
  fieldLunch: beat('lifeSitEat', 0.6, 2.4),
  sleep: still('sit'),
  nap: beat('lifeSitNap', 0.5, 4),
  sitRest: still('sit'),
  plough: beat('hoe', 0.95 / 1.5, 1.5),
  plantRice: beat('lifePlant', 0.55, 1.6),
  harvest: beat('lifeHarvest', 0.6, 1.3),
  tillSoil: beat('hoe', 0.95 / 1.5, 1.5),
  castNet: loop('lifeCast', 4.2),
  boatNet: loop('lifeCast', 4.8),
  fishRod: still('fish'),
  mendNet: still('mend'),
  washClothes: beat('lifeWash', 0.5, 1.4),
  hangLaundry: beat('lifeHang', 0.5, 2.4),
  hawk: run(
    [
      ['call', 0, 6],
      ['wave', 1, 3],
      ['call', 0, 4],
    ],
    5,
  ),
  cook: beat('lifeCook', 0.6, 1.1),
  serve: beat('lifeServe', 0.7, 3),
  sweepYard: beat('sweep', 0.5, 1.1),
  sweepShrine: beat('sweep', 0.5, 1.1),
  chopWood: beat('lifeChop', 0.6, 1.5),
  carryWood: still('haul', 0),
  tea: sipping,
  innTea: sipping,
  chess: beat('lifeSitChess', 0.8, 5),
  zither: beat('lifeSitZither', 0.5, 0.8),
  gossip: run([
    ['fold', 0, 5],
    ['point', 0, 2],
    ['fold', 0, 4],
    ['think', 0, 4],
    ['laugh', 0, 2],
  ]),
  marketChat: run([
    ['think', 0, 5],
    ['point', 0, 3],
    ['akimbo', 0, 4],
  ]),
  watchOpera: run([
    ['fold', 0, 10],
    ['clap', 0, 2],
    ['clap', 1, 2],
    ['clap', 0, 2],
    ['clap', 1, 2],
  ]),
  incense: beat('lifeIncense', 0.6, 3),
  pray: beat('lifeIncense', 0.4, 2),
  storyKids: beat('lifeSitTell', 0.5, 2.6),
  chase: still('idle'),
  hideSeek: beat('lifeSeek', 0.85, 3),
  woodSword: beat('lifeMimic', 0.55, 0.9),
  shuttle: beat('lifeKick', 0.6, 1.2),
  splash: beat('lifeSplash', 0.55, 1.8),
  helpChores: beat('sweep', 0.5, 1.3),
  spar: run(
    [
      ['lowGuard', 0, 3],
      ['windup', 0, 2],
      ['cut', 0, 2],
      ['follow', 0, 2],
      ['lowGuard', 0, 3],
      ['thrust', 0, 2],
      ['block', 0, 3],
    ],
    7,
  ),
  drillForm: {
    frames: Array.from({ length: FRAMES }, (_, i) => ['motion', motionPhase('kata', i)] as const),
    fps: (FRAMES * 3) / 8,
  },
  horseStance: beat('lifeStance', 0.5, 3.2),
  meditate: beat('lifeSitMeditate', 0.5, 4),
  meditateHall: beat('lifeSitMeditate', 0.5, 4),
  teach: beat('lifeTeach', 0.7, 3.4),
  recover: beat('lifeSitNap', 0.5, 4),
  visitSick: run([
    ['bow', 0, 4],
    ['think', 0, 6],
    ['fold', 0, 6],
  ]),
};

/** Every life act's art by its emote id, and the hide-and-seek hiders (`life-hide`). */
export const LIFE_ART: Readonly<Record<string, EmoteArt>> = {
  ...Object.fromEntries(LIFE_ACT_IDS.map((id) => [lifeEmote(id), ART[id]])),
  'life-hide': beat('lifeHide', 0.7, 1.4),
};
